import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authApi, onAdminSessionExpired } from '../lib/api';

type SessionState = 'checking' | 'authorised' | 'login';

const EXPIRED_MESSAGE = 'Your admin session ended. Sign in to pick up where you left off.';

export default function AdminGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>('checking');
  const expired = useRef(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then(() => {
        if (!cancelled) setState('authorised');
      })
      .catch(() => {
        if (!cancelled) setState('login');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The mount check alone leaves a session that expires mid-visit showing a
  // local error on whichever widget asked last, so every guarded 401 lands here
  // instead and takes the return path to the login page with it.
  useEffect(() => {
    onAdminSessionExpired(() => {
      // A page load fires several admin requests at once, so the first 401 of a
      // burst owns the message and the rest pass quietly.
      if (expired.current) return;
      expired.current = true;
      setState('login');
    });
    return () => onAdminSessionExpired(null);
  }, []);

  if (state === 'checking') {
    return (
      <main className="shell shell-centred">
        <p className="mono-label">Checking session</p>
      </main>
    );
  }
  // The message travels in navigation state rather than a toast: the only
  // Toaster lives in AdminShell, which unmounts on the way to the login page,
  // so a toast raised here would never be rendered by anyone.
  if (state === 'login') {
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{ from: location, ...(expired.current ? { message: EXPIRED_MESSAGE } : {}) }}
      />
    );
  }
  return <>{children}</>;
}
